import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'

/**
 * Maybourne Takeover Carousel Component
 * A professional carousel using Swiper.js with momentum scrolling and smooth animations
 */
export class TakeoverCarousel {
  constructor(element) {
    this.element = element
    this.swiper = null
    this.init()
  }

  init() {
    if (!this.element) return

    // Get configuration from data attributes
    const config = this.getConfig()

    // Initialize Swiper with configuration
    this.initSwiper(config)

    // Setup event listeners
    this.setupEventListeners()
  }

  getConfig() {
    const element = this.element
    
    return {
      // Core settings - auto width for better control
      slidesPerView: 'auto', // Auto-detect based on slide width
      spaceBetween: 30, // Gap between cards
      centeredSlides: false,
      loop: false,
      speed: 600,
      initialSlide: 0,
      slidesPerGroup: 1, // Move exactly one slide per arrow click
      
      // Offset slides from the left edge (about 1/3 of container width)
      slidesOffsetBefore: 0, // Default no offset - will be overridden by breakpoints
      
      // Better scrolling behavior
      freeMode: true, // Enable free-flowing drag without snapping
      freeModeSticky: true, // Add slight snapping at end of momentum
      freeModeMomentum: true, // Enable momentum scrolling
      freeModeMomentumRatio: 1, // Natural momentum feel
      freeModeMomentumBounce: false, // Disable bouncing at edges
      slideToClickedSlide: true,
      allowTouchMove: true,
      threshold: 5, // Reduce threshold for easier swiping
      
      // Touch settings for smooth momentum
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      touchMoveStopPropagation: false,
      
      // Allow slides to extend beyond container
      watchOverflow: false,
      centerInsufficientSlides: true, // Center slides when there are fewer than slidesPerView
      
      // Responsive spacing and offset
      breakpoints: {
        // Mobile - clean edge-to-edge with no offset
        320: {
          spaceBetween: 20,
          slidesPerGroup: 1,
          slidesPerView: 1,
          slidesOffsetBefore: 0 // No offset - start from left edge
        },
        // Small mobile
        480: {
          spaceBetween: 20,
          slidesPerGroup: 1,
          slidesPerView: 1,
          slidesOffsetBefore: 0 // No offset - start from left edge
        },
        // Small tablet - start adding offset back
        768: {
          spaceBetween: 25,
          slidesPerGroup: 1,
          slidesOffsetBefore: 120 // Medium offset
        },
        // Tablet and up
        900: {
          spaceBetween: 30,
          slidesPerGroup: 1,
          slidesOffsetBefore: 160 // Larger offset on desktop
        },
        // Large desktop
        1200: {
          spaceBetween: 30,
          slidesPerGroup: 1,
          slidesOffsetBefore: 200 // Even larger offset on large screens
        }
      },
      
      // Autoplay
      autoplay: element.dataset.autoPlay === 'true' ? {
        delay: parseInt(element.dataset.autoPlaySpeed) || 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,

      // Navigation
      navigation: {
        nextEl: element.querySelector('.takeover-carousel__nav--next'),
        prevEl: element.querySelector('.takeover-carousel__nav--prev'),
        disabledClass: 'takeover-carousel__nav--disabled'
      },

      // Pagination
      pagination: {
        el: '.takeover-carousel__pagination',
        type: 'bullets',
        clickable: true,
        bulletClass: 'takeover-carousel__pagination-bullet',
        bulletActiveClass: 'takeover-carousel__pagination-bullet--active',
        dynamicBullets: false, // Disable dynamic bullets for consistent display
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>';
        }
      },

      // Modules
      modules: [Navigation, Pagination, Autoplay],

      // Events
      on: {
        init: () => {
          this.onInit()
        },
        slideChange: () => {
          this.onSlideChange()
        },
        touchStart: () => {
          this.onTouchStart()
        },
        touchEnd: () => {
          this.onTouchEnd()
        }
      }
    }
  }

  initSwiper(config) {
    // Find swiper container
    const swiperContainer = this.element.querySelector('.takeover-carousel__swiper')
    
    if (!swiperContainer) {
      console.error('Takeover Carousel: Swiper container not found')
      return
    }

    try {
      this.swiper = new Swiper(swiperContainer, config)
    } catch (error) {
      console.error('Takeover Carousel: Failed to initialize Swiper', error)
    }
  }

  setupEventListeners() {
    // Pause autoplay on hover
    if (this.swiper?.autoplay) {
      this.element.addEventListener('mouseenter', () => {
        this.swiper.autoplay.pause()
      })

      this.element.addEventListener('mouseleave', () => {
        this.swiper.autoplay.resume()
      })
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isVisible()) return

      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.swiper?.slidePrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.swiper?.slideNext()
      }
    })

    // Window resize listener for dynamic product info positioning
    let resizeTimeout
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        this.updateProductInfoPositions()
      }, 150) // Debounce resize events
    })
  }

  // Event handlers
  onInit() {
    this.element.classList.add('takeover-carousel--initialized')
    // Set dynamic product info positions after initialization
    this.updateProductInfoPositions()
    // Initialize quote first-word styling
    this.initQuoteFirstWord()
  }

  onSlideChange() {
    // Update any custom indicators or states
    const activeSlide = this.swiper?.slides[this.swiper.activeIndex]
    if (activeSlide) {
      // Trigger any slide-specific animations or updates
      this.element.dispatchEvent(new CustomEvent('slideChange', {
        detail: { activeIndex: this.swiper.activeIndex, activeSlide }
      }))
    }
  }

  onTouchStart() {
    this.element.classList.add('takeover-carousel--touching')
  }

  onTouchEnd() {
    this.element.classList.remove('takeover-carousel--touching')
  }

  // Quote styling methods
  initQuoteFirstWord() {
    // Find all quote elements in this carousel
    const quoteElements = this.element.querySelectorAll('.takeover-carousel__quote')
    
    quoteElements.forEach(quote => {
      // Get the original HTML content to preserve <br> tags
      const originalHTML = quote.innerHTML.trim()
      if (!originalHTML) return
      
      // Get plain text to work with words
      const text = quote.textContent.trim()
      const words = text.split(/\s+/)
      if (words.length === 0) return
      
      // Get the first word
      const firstWord = words[0]
      if (firstWord.length === 0) return
      
      // Split the first word into first letter and rest
      const firstLetter = firstWord.charAt(0)
      const restOfFirstWord = firstWord.slice(1)
      
      // Create the nested span structure with curly quotes
      const openingQuote = '<span class="opening-quote">\u201C</span>'
      const firstWordSpan = `<span class="first-word"><span class="first-letter">${firstLetter}</span><span class="first-word-rest">${restOfFirstWord}</span></span>`
      const closingQuote = '<span class="closing-quote">\u201D</span>'
      
      // Create a regex to find and replace the first word in the HTML
      // This preserves any <br> tags or other formatting
      const firstWordRegex = new RegExp(`^(\\s*)${firstWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|<br|$)`, 'i')
      
      if (firstWordRegex.test(originalHTML)) {
        // Replace the first word with opening quote + styled first word
        let newHTML = originalHTML.replace(firstWordRegex, (match, leadingSpace, followingChar) => {
          return `${leadingSpace}${openingQuote}${firstWordSpan}${followingChar}`
        })
        
        // Add closing quote at the very end
        newHTML = newHTML + closingQuote
        
        quote.innerHTML = newHTML
      }
    })
  }

  // Utility methods
  isVisible() {
    const rect = this.element.getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
  }

  updateProductInfoPositions() {
    // Skip dynamic positioning on mobile breakpoints (768px and below) - let CSS handle it
    if (window.innerWidth <= 768) return
    
    // Find all product info elements in this carousel
    const productInfoElements = this.element.querySelectorAll('.takeover-carousel__product-info')
    const swiperContainer = this.element.querySelector('.takeover-carousel__swiper')
    const carouselContainer = this.element.querySelector('.takeover-carousel__container')
    
    if (!swiperContainer || !carouselContainer) return
    
    let maxHeight = 0
    
    // First pass: reset all positions and measure heights
    productInfoElements.forEach(infoElement => {
      // Clear any existing inline styles that might affect measurement
      infoElement.style.bottom = ''
      infoElement.style.position = ''
      infoElement.style.transform = ''
    })
    
    // Force a reflow to get accurate measurements
    this.element.offsetHeight
    
    // Second pass: measure and position
    productInfoElements.forEach(infoElement => {
      // Measure the actual height of the content in its natural state
      const height = infoElement.offsetHeight
      
      // Track the tallest product info
      maxHeight = Math.max(maxHeight, height)
      
      // Set absolute positioning and bottom value based on actual height
      infoElement.style.position = 'absolute'
      infoElement.style.bottom = `-${height}px`
    })
    
    // Update carousel bottom margin based on tallest product info
    // Add some extra padding (8px) for breathing room
    const paginationElements = carouselContainer.querySelectorAll('.swiper-pagination-bullets')
    
    if (maxHeight > 0) {
      const dynamicMargin = maxHeight + 8 // 8px extra padding
      swiperContainer.style.marginBottom = `${dynamicMargin}px`
      
      // Update pagination positioning to account for product info height
      const dynamicPaginationBottom = -(maxHeight / 16) // Negative product info height in rem
      paginationElements.forEach(pagination => {
        pagination.style.setProperty('bottom', `${dynamicPaginationBottom}rem`, 'important')
      })
    } else {
      // No product slides, use minimal margin and default pagination positioning
      swiperContainer.style.marginBottom = '1rem'
      paginationElements.forEach(pagination => {
        pagination.style.setProperty('bottom', '-3.5rem', 'important')
      })
    }
  }

  // Public API methods
  slideTo(index, speed = 300) {
    this.swiper?.slideTo(index, speed)
  }

  slideNext() {
    this.swiper?.slideNext()
  }

  slidePrev() {
    this.swiper?.slidePrev()
  }

  play() {
    this.swiper?.autoplay?.start()
  }

  pause() {
    this.swiper?.autoplay?.stop()
  }

  // Cleanup
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
    
    this.element.classList.remove('takeover-carousel--initialized', 'takeover-carousel--touching')
  }
}

export default TakeoverCarousel 